import {
  buildAndroidAssetLinks as buildAndroidAssetLinksFromAdapter,
  buildAppleAppSiteAssociation as buildAppleAppSiteAssociationFromAdapter,
  buildBrowserContinuationUrl as buildBrowserContinuationUrlFromAdapter,
  buildMobileHandoffUrl as buildMobileHandoffUrlFromAdapter,
  buildNativeBrowserLoginUrl as buildNativeBrowserLoginUrlFromAdapter,
  buildNativeCallbackUrl as buildNativeCallbackUrlFromAdapter,
  decodeTrialHandoffSnapshot,
  getNativeBridgeTarget as getNativeBridgeTargetFromAdapter,
  parseBrowserHandoff as parseBrowserHandoffFromAdapter,
  TRIAL_HANDOFF_PARAM,
  type BrowserHandoffTarget,
  type MobilePlatform,
  type MobileSessionPayload,
  type NativeBridgeTarget,
} from "@fluentry/platform-adapters";
import { clientEnv } from "./client-env";

export type AppEnvironment = "dev" | "staging" | "prod";

export type MobileBridgeConfig = {
  appEnv: AppEnvironment;
  appOrigin: string;
  apiOrigin: string;
  publicWebOrigin: string;
  redirectUri: string;
  handoffPath: string;
  appStoreUrl: string | null;
  playStoreUrl: string | null;
  associatedHost: string | null;
  associatedDomains: string[];
  appLinkHosts: string[];
  customSchemes: string[];
  appId: string;
};

export type BrowserHandoffIntent = "login" | "workbench";
export { decodeTrialHandoffSnapshot, TRIAL_HANDOFF_PARAM };

export const PRODUCT_APP_ID = "com.fluentry.app";
export const APPLE_TEAM_ID_PLACEHOLDER = "TEAMID";
export const ANDROID_CERT_FINGERPRINT_PLACEHOLDER = "SHA256_CERT_FINGERPRINT";

const PROD_HOSTS = {
  app: "app.fluentry.ai",
  api: "api.fluentry.ai",
  publicWeb: "www.fluentry.ai",
};

const STAGING_HOSTS = {
  app: "app.staging.fluentry.ai",
  api: "api.staging.fluentry.ai",
  publicWeb: "www.staging.fluentry.ai",
};

const DEV_DEFAULTS = {
  appOrigin: "http://localhost:3001",
  apiOrigin: "http://localhost:8787",
};

const DEFAULT_HANDOFF_PATH = "/open";
const DEFAULT_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.fluentry.app";

function normalizeUrl(value: string | undefined | null, fallback: string): string {
  if (!value || value.length === 0) {
    return fallback;
  }

  try {
    return new URL(value).toString().replace(/\/$/, "");
  } catch {
    return fallback;
  }
}

export function normalizeAppEnvironment(value: string | null | undefined): AppEnvironment | null {
  if (value === "dev" || value === "staging" || value === "prod") {
    return value;
  }

  return null;
}

export function inferAppEnvironmentFromOrigin(origin: string): AppEnvironment {
  if (!origin || origin.startsWith("capacitor://") || origin.startsWith("ionic://")) {
    return normalizeAppEnvironment(clientEnv.VITE_APP_ENV) ?? (clientEnv.DEV ? "dev" : "prod");
  }

  try {
    const hostname = new URL(origin).hostname;
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.endsWith(".localhost") ||
      hostname.endsWith(".local")
    ) {
      return "dev";
    }

    if (hostname.endsWith(".staging.fluentry.ai")) {
      return "staging";
    }

    return "prod";
  } catch {
    return normalizeAppEnvironment(clientEnv.VITE_APP_ENV) ?? (clientEnv.DEV ? "dev" : "prod");
  }
}

function resolveAppOrigin(appEnv: AppEnvironment, runtimeOrigin: string): string {
  const override = clientEnv.VITE_APP_ORIGIN;
  if (override) {
    return normalizeUrl(
      override,
      appEnv === "dev"
        ? DEV_DEFAULTS.appOrigin
        : `https://${appEnv === "staging" ? STAGING_HOSTS.app : PROD_HOSTS.app}`,
    );
  }

  if (appEnv === "dev") {
    if (runtimeOrigin && runtimeOrigin.startsWith("http")) {
      return normalizeUrl(runtimeOrigin, DEV_DEFAULTS.appOrigin);
    }

    return DEV_DEFAULTS.appOrigin;
  }

  return appEnv === "staging" ? `https://${STAGING_HOSTS.app}` : `https://${PROD_HOSTS.app}`;
}

function resolveApiOrigin(appEnv: AppEnvironment): string {
  const override = clientEnv.VITE_API_BASE_URL;
  if (override) {
    return normalizeUrl(
      override,
      appEnv === "dev"
        ? DEV_DEFAULTS.apiOrigin
        : `https://${appEnv === "staging" ? STAGING_HOSTS.api : PROD_HOSTS.api}`,
    );
  }

  if (appEnv === "dev") {
    return DEV_DEFAULTS.apiOrigin;
  }

  return appEnv === "staging" ? `https://${STAGING_HOSTS.api}` : `https://${PROD_HOSTS.api}`;
}

function resolvePublicWebOrigin(appEnv: AppEnvironment): string {
  const override = clientEnv.VITE_PUBLIC_WEB_ORIGIN;
  if (override) {
    return normalizeUrl(
      override,
      appEnv === "dev"
        ? "http://localhost:3000"
        : `https://${appEnv === "staging" ? STAGING_HOSTS.publicWeb : PROD_HOSTS.publicWeb}`,
    );
  }

  if (appEnv === "dev") {
    return "http://localhost:3000";
  }

  return appEnv === "staging"
    ? `https://${STAGING_HOSTS.publicWeb}`
    : `https://${PROD_HOSTS.publicWeb}`;
}

function resolveRedirectUri(appEnv: AppEnvironment): string {
  switch (appEnv) {
    case "staging":
      return "fluentry-staging://auth/callback";
    case "prod":
      return "fluentry://auth/callback";
    default:
      return "fluentry-dev://auth/callback";
  }
}

export function resolveMobileBridgeConfig(
  runtimeOrigin = typeof window !== "undefined" ? window.location.origin : "",
): MobileBridgeConfig {
  const appEnv =
    normalizeAppEnvironment(clientEnv.VITE_APP_ENV) ?? inferAppEnvironmentFromOrigin(runtimeOrigin);

  return {
    appEnv,
    appOrigin: resolveAppOrigin(appEnv, runtimeOrigin),
    apiOrigin: resolveApiOrigin(appEnv),
    publicWebOrigin: resolvePublicWebOrigin(appEnv),
    redirectUri: resolveRedirectUri(appEnv),
    handoffPath: clientEnv.VITE_MOBILE_HANDOFF_PATH ?? DEFAULT_HANDOFF_PATH,
    appStoreUrl: clientEnv.VITE_APP_STORE_URL ?? null,
    playStoreUrl: clientEnv.VITE_PLAY_STORE_URL ?? DEFAULT_PLAY_STORE_URL,
    associatedHost:
      appEnv === "dev" ? null : appEnv === "staging" ? STAGING_HOSTS.app : PROD_HOSTS.app,
    associatedDomains: [`applinks:${PROD_HOSTS.app}`, `applinks:${STAGING_HOSTS.app}`],
    appLinkHosts: [PROD_HOSTS.app, STAGING_HOSTS.app],
    customSchemes: ["fluentry", "fluentry-staging", "fluentry-dev"],
    appId: PRODUCT_APP_ID,
  };
}

export function getNativeBridgeTarget(search: string): NativeBridgeTarget | null {
  return getNativeBridgeTargetFromAdapter(search);
}

export function buildNativeCallbackUrl(redirectUri: string, session: MobileSessionPayload): string {
  return buildNativeCallbackUrlFromAdapter(redirectUri, session);
}

export function buildNativeBrowserLoginUrl(
  config: MobileBridgeConfig,
  platform: MobilePlatform,
): string {
  return buildNativeBrowserLoginUrlFromAdapter(config, platform);
}

export function parseBrowserHandoff(search: string): BrowserHandoffTarget {
  return parseBrowserHandoffFromAdapter(search);
}

export function buildMobileHandoffUrl(
  config: MobileBridgeConfig,
  target: BrowserHandoffTarget,
): string {
  return buildMobileHandoffUrlFromAdapter(config, target);
}

export function buildBrowserContinuationUrl(
  config: MobileBridgeConfig,
  target: BrowserHandoffTarget,
  authenticated: boolean,
): string {
  return buildBrowserContinuationUrlFromAdapter(config, target, authenticated);
}

export function hasStoreListingUrl(url: string | null): url is string {
  // Store links should be absent until a real listing exists; placeholder URLs are not externally actionable.
  return typeof url === "string" && url.length > 0;
}

export function buildAppleAppSiteAssociation() {
  return buildAppleAppSiteAssociationFromAdapter(PRODUCT_APP_ID, APPLE_TEAM_ID_PLACEHOLDER);
}

export function buildAndroidAssetLinks() {
  return buildAndroidAssetLinksFromAdapter(PRODUCT_APP_ID, ANDROID_CERT_FINGERPRINT_PLACEHOLDER);
}
